import { formatCompactValue, toneColor, asNumber } from "../../lib/kernelFormat.js";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { HeroNumber, KernelEmpty } from "./shared.jsx";

export const kind = "kpis";

// Multi-tile KPI row. Two data shapes supported:
//   • single object (data is a {field: val} map) — every tile pulls its
//     own valueField/deltaField from that same object.
//   • array (data[i] is the source row for tiles[i]) — tile/data zip.
// We pick by checking data shape so the LLM can use whichever feels
// natural given the upstream step.
export function Component({ spec, data }) {
    const tiles = Array.isArray(spec.tiles) ? spec.tiles : [];
    if (!tiles.length) return <KernelEmpty message={spec.empty || "no tiles"} />;

    const rows = Array.isArray(data) ? data : null;

    return (
        <div
            className="grid gap-[8px]"
            style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}
        >
            {tiles.map((tile, i) => {
                // A one-row result feeds every tile. Per-tile rows stay the
                // behaviour for a multi-row result; without this a summary had
                // to flatten itself in a `python` step, which needs the
                // isolated Python realm no deployed environment configures.
                const source = rows ? (rows.length === 1 ? rows[0] : rows[i]) : data;
                // Same guard as KernelStat — only pull a field when it
                // was actually named, otherwise pickFrom returns the
                // whole source object and we render "[object Object]".
                const value = tile.valueField ? pickFrom(source, tile.valueField) : pickFrom(source, null);
                const delta = tile.deltaField ? pickFrom(source, tile.deltaField) : null;
                const deltaText = delta != null ? formatCompactValue(delta, tile.deltaFormat || tile.format) : null;
                // Multi-word labels wrap at the space to two lines so
                // "CACHE READS"/"CACHE WRITES" stay distinct; a single word
                // (e.g. "COVERAGE") uses single-line truncate, which paints a
                // proper "COVERA…" ellipsis instead of a hard clip.
                const labelLines = (tile.label || "").trim().includes(" ") ? 2 : 1;
                const deltaTone = (() => {
                    const n = asNumber(delta);
                    if (n == null) return "muted";
                    if (tile.tone) return tile.tone;
                    return n > 0 ? "good" : n < 0 ? "bad" : "muted";
                })();
                return (
                    <div
                        key={i}
                        className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[12px] py-[10px]"
                    >
                        {/* Multi-word labels wrap to two lines (keeps "CACHE
                            READS"/"CACHE WRITES" distinct); a single word
                            truncates with an ellipsis + hover tooltip rather
                            than clipping or breaking mid-word. Reserved 2-line
                            height keeps hero numbers baseline-aligned. */}
                        <OverflowTooltip
                            as="div"
                            lines={labelLines}
                            text={tile.label || ""}
                            className="text-[10px] uppercase leading-[1.2] tracking-[0.12em] text-[color:var(--muted-soft)] min-h-[24px]"
                        />
                        <div className="mt-[4px] flex items-baseline gap-[8px]">
                            <HeroNumber
                                value={value}
                                format={tile.format}
                                tone={tile.tone}
                                className="font-mono text-[18px] leading-[1.05] text-[color:var(--ink)]"
                            />
                            {deltaText != null ? (
                                <span
                                    className="text-[10.5px]"
                                    style={{ color: toneColor(deltaTone) }}
                                >
                                    {Number(delta) > 0 ? "▲" : Number(delta) < 0 ? "▼" : "·"} {deltaText}
                                </span>
                            ) : null}
                        </div>
                        {tile.sublabel ? (
                            <OverflowTooltip as="div" text={String(tile.sublabel)} className="mt-[2px] text-[10px] text-[color:var(--muted-soft)]" />
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function pickFrom(source, field) {
    if (source == null) return null;
    if (!field) return typeof source === "object" && !Array.isArray(source) ? source.value ?? source : source;
    if (typeof source === "object") return source[field];
    return source;
}
